import { useEffect, useRef } from "react";

import { FullScreenLoader } from "sj-ui-toolkit/src/components";
import { useSegmentTrackEvent } from "sj-ui-toolkit/src/hooks";
import type { TournamentLessonResponse } from "sj-ui-toolkit/src/api";
import type { SandboxLessonDetails } from "sj-ui-toolkit/src/client";

import { useLessonLoader } from "../../../hooks/useLessonLoader";
import { useTournamentContext } from "../../../context/TournamentContext";
import SandboxLesson from "../../Lesson/SandboxLesson";
import VideoLesson from "../../Lesson/VideoLesson";
import type { VideoLessonDetails } from "../../Lesson/VideoLesson/VideoProvider";

/**
 * TournamentLesson Component
 *
 * Renders a lesson within a tournament context. Uses the useLessonLoader hook
 * to fetch and display lesson details. Handles both video and sandbox (break/fix)
 * lesson types.
 */
export default function TournamentLesson({ onContentComplete }: { onContentComplete: () => void }) {
  const { content, contentType, exerciseContent, subscribeToChannel } = useTournamentContext();

  // lesson key is only present on the Lesson content type, not Experiment
  const lessonKey =
    contentType === "Lesson"
      ? (content as TournamentLessonResponse)?.lesson?.lesson?.lessonKey
      : undefined;
  const tournamentUuid = content?.tournament?.publicId;

  // Use the lesson loader hook
  const { lessonDetails } = useLessonLoader({
    lessonKey: lessonKey ?? null,
    tournamentContent: content,
    exerciseContent: exerciseContent as Parameters<typeof useLessonLoader>[0]["exerciseContent"],
  });

  const trackEvent = useSegmentTrackEvent();
  const hasTrackedLaunch = useRef(false);
  const lessonCompletedRef = useRef(false);
  const launchTimeRef = useRef<number | null>(null);
  const trackEventRef = useRef(trackEvent);
  const lessonKeyRef = useRef(lessonKey);
  trackEventRef.current = trackEvent;
  lessonKeyRef.current = lessonKey;

  // Send Segment event when the tournament lesson page is visited
  useEffect(() => {
    if (lessonDetails && !hasTrackedLaunch.current) {
      trackEvent("Lesson launch", { lessonKey, context: "tournament" });
      hasTrackedLaunch.current = true;
      launchTimeRef.current = Date.now();
    }
  }, [lessonDetails, lessonKey, trackEvent]);

  // Send bounce/abandoned event on unmount
  useEffect(() => {
    return () => {
      if (!launchTimeRef.current || lessonCompletedRef.current) return;
      const elapsed = Date.now() - launchTimeRef.current;
      const event = elapsed < 30000 ? "Lesson bounce" : "Lesson abandoned";
      trackEventRef.current(event, { lessonKey: lessonKeyRef.current, context: "tournament" });
    };
  }, []);

  // Set up ActionCable subscriptions for tournament context
  useEffect(() => {
    if (lessonDetails) {
      if (lessonDetails.isBreakFix) {
        switch (lessonDetails.completionType) {
          case "tests":
            subscribeToChannel("tests");
            break;
          case "flagSubmission":
            subscribeToChannel("ctf");
            break;
          default:
            subscribeToChannel("default");
            break;
        }
      }
    }
  }, [lessonDetails, subscribeToChannel]);

  const onLessonFinish = () => {
    lessonCompletedRef.current = true;
    trackEvent("Lesson completed", { lessonKey, context: "tournament" });
    onContentComplete();
  };

  if (!lessonDetails) {
    return (
      <div className="h-full">
        <FullScreenLoader />
      </div>
    );
  }

  // useLessonLoader merges Rails + svc-content into a loose aggregate; the runtime object matches
  // each lesson type's shape. TODO(SHRPA-4120): type the loader per lesson type to drop this cast.
  const details = lessonDetails as unknown;

  if (lessonDetails.isBreakFix) {
    return (
      <SandboxLesson
        lessonDetails={details as SandboxLessonDetails}
        tournamentUuid={tournamentUuid}
        onLessonFinish={onLessonFinish}
      />
    );
  } else {
    return (
      <VideoLesson
        lessonDetails={details as VideoLessonDetails}
        tournamentUuid={tournamentUuid}
        onLessonFinish={onLessonFinish}
        exerciseOnly={contentType === "Experiment"}
      />
    );
  }
}
